import {
  Checkbox,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  useTheme,
} from "@mui/material";
import { rolePermissionsBusiness } from "api/bussiness/user-role-permission/role-permission-bussiness";
import { observer } from "mobx-react-lite";
import { getParent } from "mobx-state-tree";
import {
  IPermisionGroup,
  IPermission,
} from "mobx-stores/_models/user-roles/permission";
import { useStores } from "mobx-stores/_root-store";
import { Fragment, useCallback, useEffect } from "react";
import { DialogForm } from "share-components/templates/form/dialog-form";

export const AssignPermission = observer(() => {
  const {
    rolePermissionManagement: {
      openAssignPermissions,
      permissionCollection,
      roleSelected,
      setProp,
      getFlatPermissions,
    },
  } = useStores();

  const saveHandler = useCallback(async () => {
    if (!permissionCollection?.entityDisplayName) {
      return;
    }
    const flatPermissions = getFlatPermissions();
    await rolePermissionsBusiness.updateRolePermissions(
      permissionCollection?.entityDisplayName,
      {
        permissions: flatPermissions
          ? flatPermissions.map((x) => ({
              isGranted: x.isGranted || false,
              name: x.name || "",
            }))
          : [],
      }
    );
    setProp("openAssignPermissions", false);
  }, [permissionCollection?.entityDisplayName, getFlatPermissions, setProp]);

  return (
    <DialogForm
      open={openAssignPermissions}
      fullWidth
      PaperProps={{
        sx: {
          height: "80%",
        },
      }}
      maxWidth={"md"}
      onCancelBtnClicked={() => {
        setProp("openAssignPermissions", false);
      }}
      onSaveBtnClick={saveHandler}
    >
      <DialogTitle>
        {`Phân quyền cho vai trò ${roleSelected?.name || ""}`}
      </DialogTitle>
      <DialogContent>
        <PermisonList></PermisonList>
      </DialogContent>
    </DialogForm>
  );
});

const PermisonList = observer(() => {
  const {
    rolePermissionManagement: {
      permissionCollection,
      getPermissionCollectionByRoleName,
      roleSelected,
    },
  } = useStores();

  useEffect(() => {
    if (roleSelected?.id) {
      getPermissionCollectionByRoleName(roleSelected?.name ?? "");
    }
  }, [getPermissionCollectionByRoleName, roleSelected?.id, roleSelected?.name]);

  return (
    <Stack>
      {permissionCollection &&
        permissionCollection?.groups.map((x, idx) => (
          <Fragment key={x.name}>
            <PermisionGroup itemGroup={x}></PermisionGroup>
            {idx !== permissionCollection.groups.length - 1 && (
              <Divider></Divider>
            )}
          </Fragment>
        ))}
    </Stack>
  );
});
type PermisionGroupProps = {
  itemGroup: IPermisionGroup;
};
const PermisionGroup = observer(({ itemGroup }: PermisionGroupProps) => {
  return (
    <List
      component={"div"}
      role="list"
      subheader={
        <ListSubheader component="div">{itemGroup.displayName}</ListSubheader>
      }
    >
      {itemGroup.permissions.map((x) => (
        <PermisionItem item={x} key={x.name}></PermisionItem>
      ))}
    </List>
  );
});
type PermisionItemProps = {
  item: IPermission;
};
const PermisionItem = observer(({ item }: PermisionItemProps) => {
  const theme = useTheme();
  const recursiveCheckParents = useCallback((item: IPermission) => {
    if (!item.parentName) return;
    const permissions = getParent(item) as IPermission[];
    const parentItem = permissions.filter(
      (x) => x.name === item.parentName
    )?.[0];
    parentItem.setProp("isGranted", true);
    recursiveCheckParents(parentItem);
  }, []);

  const recursiveUncheckChildren = useCallback((item: IPermission) => {
    const permissions = getParent(item) as IPermission[];
    const childrenItem = permissions.filter(
      (x) => item.name && x.name?.includes(item.name)
    );
    childrenItem.forEach((x) => {
      x.setProp("isGranted", false);
    });
  }, []);

  const grantChanged = useCallback(
    (checked: boolean) => {
      item.setProp("isGranted", checked);
      if (checked) {
        recursiveCheckParents(item);
      } else {
        recursiveUncheckChildren(item);
      }
    },
    [item, recursiveCheckParents, recursiveUncheckChildren]
  );
  return (
    <ListItem
      disablePadding
      sx={{
        paddingLeft: `${
          item.name && (item.name?.split(".").length - 1) * 40
        }px`,
      }}
    >
      <ListItemButton
        role={undefined}
        onClick={() => {
          grantChanged(!item.isGranted);
        }}
        disableTouchRipple
        disableRipple
      >
        <ListItemText
          primaryTypographyProps={{
            sx: {
              fontWeight:
                item.parentName == null
                  ? theme.typography.fontWeightBold
                  : undefined,
            },
          }}
          primary={item.displayName}
        />
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={item.isGranted}
            onChange={(e, checked) => {
              grantChanged(checked);
            }}
            tabIndex={-1}
            disableRipple
          />
        </ListItemIcon>
      </ListItemButton>
    </ListItem>
  );
});
